// @flow
import { Backbone } from "extensions/backbone";
import Radio from "backbone.radio";
import { captureException } from "extensions/sentry";
import { toast } from "extensions/toast";

import { store } from "common/store";
import { merchant } from "common/config/api";
import strings from "common/strings";
import { request } from "common/components/request";

export const MerchantModel = Backbone.Model.extend({
    idAttribute: "_id",
    url: merchant.settings,
    
    defaults: () => ({
        default_currency: "USD",
        date_range: [],
        default_group: null,
        has_trans_search: false,
        has_pazien_login: true,
        business_units: [],
        root_merchant: null
    }),
    
    /**
     * Get provider-type mapping or type of provider if one requested and found in the dictionary
     * @param {String} [ref] optional provider to find it's type
     * @returns {Object | String}
     */
    getProvidersByType(ref) {
        const allProvidersByType = this.getRootMerchantProperty("processors");
        
        if (typeof ref === "string") {
            const provider = allProvidersByType.find(({ name }) => name === ref);
            return provider ? provider.type : strings.notAvailableValue;
        }
    
        return allProvidersByType.reduce((memo, val) => ({
            ...memo,
            [val.name]: val.type
        }), {});
    },
    
    getRootMerchantProperty(property) {
        return this.getRootMerchantSettings()[property];
    },
    
    getRootMerchantSettings() {
        if (this.get("root_merchant") === null) {
            return this.toJSON();
        }
        return this.get("root_merchant");
    },
    
    isRootMerchant(): boolean {
        return this.get("root_merchant") === null;
    },
    
    // Dimensions are all unique pivots that belong to an active merchant
    // Name of a dimension should be the same as a pivot name used in api.js
    setMerchantDimensions() {
        store.dispatch({
            type: "LOADING_MERCHANT_DIMENSIONS"
        });
        
        this.dimensions = request("/merchant/:merchant_ref/processor/dimensions").then((data) => {
            // Add business units if needed
            const rootMerchant = this.getRootMerchantSettings();
            if (rootMerchant.business_units?.length) {
                const entities = Radio.channel("main").request("get:user")?.entities;
        
                // Go through all entities that the user has access to, and find tree of selected entity
                const tree = entities.findNode(store.getState().common.bu_ref || store.getState().common.merchant_ref).getAll();
        
                // Remove very first item as it is the highest possible node (All levels)
                tree.shift();
        
                // Only include display_name in the list
                data.bu = tree.map(({ display_name }) => display_name);
            }
    
            store.dispatch({
                type: "SET_MERCHANT_DIMENSIONS",
                data
            });
            
            return data;
        });
        
        return this.dimensions;
    },
    
    getDimension(name: DimensionNameType, showError: boolean = true) {
        if (this.dimensions.isFulfilled()) {
            const value = this.dimensions.value();
            if (value && Object.prototype.hasOwnProperty.call(value, name)) {
                return value[name];
            }
            
            if (showError) {
                toast.error(`Requested value '${name}' is not set in merchant dimensions`);
            }
            
            captureException(new Error("Requested value is not set in merchant dimensions"), {
                extra: {
                    name
                }
            });
        }
        
        return void 0;
    }
});
